/**
 * Material invoice PDF parsing (Aug 21, 2026).
 *
 * Extracts text from a distributor invoice PDF (RoofHub / SRS) client-side and
 * best-effort parses it into the review form's shape. NOTHING auto-commits —
 * the parse always lands in a human review form, so a layout change degrades
 * to "more typing", never to silent bad data.
 *
 * pdfjs-dist is loaded lazily from esm.sh (same zero-bundle-weight CDN pattern
 * as heicToJpeg's heic2any import — pinned version, promise-cached). Scanned /
 * photographed PDFs have no text layer → extractPdfText returns "", and the
 * panel falls through to manual entry.
 */

import type { MaterialInvoiceLine } from "./types";

const PDFJS_URL = "https://esm.sh/pdfjs-dist@4.10.38";
const PDFJS_WORKER_URL = "https://esm.sh/pdfjs-dist@4.10.38/build/pdf.worker.min.mjs";

type PdfJsModule = {
  GlobalWorkerOptions: { workerSrc: string };
  getDocument: (opts: { data: ArrayBuffer }) => { promise: Promise<PdfDoc> };
};
type PdfDoc = { numPages: number; getPage: (n: number) => Promise<PdfPage> };
type PdfPage = { getTextContent: () => Promise<{ items: Array<{ str?: string; transform?: number[] }> }> };

let pdfjsPromise: Promise<PdfJsModule> | null = null;
function loadPdfjs(): Promise<PdfJsModule> {
  if (!pdfjsPromise) {
    pdfjsPromise = import(/* @vite-ignore */ PDFJS_URL).then((mod: PdfJsModule) => {
      mod.GlobalWorkerOptions.workerSrc = PDFJS_WORKER_URL;
      return mod;
    });
  }
  return pdfjsPromise;
}

/**
 * Extract text with row structure preserved: pdf.js returns positioned glyph
 * runs, so we group items by their y-coordinate (rounded) and join each row
 * left-to-right. Line-item parsing depends on this — a naive `items.join(" ")`
 * scrambles table columns.
 */
export async function extractPdfText(file: File): Promise<string> {
  try {
    const pdfjs = await loadPdfjs();
    const doc = await pdfjs.getDocument({ data: await file.arrayBuffer() }).promise;
    const pages: string[] = [];
    for (let p = 1; p <= doc.numPages; p++) {
      const page = await doc.getPage(p);
      const content = await page.getTextContent();
      // Group by y (transform[5]); tolerance 2.5pt handles sub/superscripts.
      const rows: Array<{ y: number; parts: Array<{ x: number; s: string }> }> = [];
      for (const it of content.items) {
        const s = (it.str ?? "").trim();
        if (!s) continue;
        const x = it.transform?.[4] ?? 0;
        const y = it.transform?.[5] ?? 0;
        let row = rows.find((r) => Math.abs(r.y - y) < 2.5);
        if (!row) { row = { y, parts: [] }; rows.push(row); }
        row.parts.push({ x, s });
      }
      rows.sort((a, b) => b.y - a.y); // top of page first
      pages.push(rows.map((r) => r.parts.sort((a, b) => a.x - b.x).map((p2) => p2.s).join("  ")).join("\n"));
    }
    return pages.join("\n\n");
  } catch (e) {
    console.warn("[materialInvoiceParse] pdf text extraction failed", e);
    return "";
  }
}

export interface ParsedInvoice {
  invoice_number: string | null;
  order_number: string | null;
  invoice_date: string | null; // yyyy-mm-dd
  due_date: string | null;
  terms: string | null;
  job_name: string | null;
  job_address: string | null;
  subtotal: number | null;
  tax: number | null;
  total: number | null;
  line_items: MaterialInvoiceLine[];
  /** how many of the headline fields the parser found — the panel shows this */
  fields_found: number;
}

function usDateToIso(s: string): string | null {
  const m = s.match(/^(\d{1,2})[\/-](\d{1,2})[\/-](\d{2,4})$/);
  if (!m) return null;
  const yr = m[3].length === 2 ? "20" + m[3] : m[3];
  return `${yr}-${m[1].padStart(2, "0")}-${m[2].padStart(2, "0")}`;
}

function money(s: string | undefined | null): number | null {
  if (!s) return null;
  const n = Number(String(s).replace(/[$,]/g, ""));
  return Number.isFinite(n) ? n : null;
}

const UOMS = ["EA", "BD", "BDL", "BX", "RL", "PC", "SQ", "CT", "PL", "TB", "PR", "ST", "LF"];

/**
 * Best-effort parse of an SRS / RoofHub invoice's extracted text. Every field
 * is optional — the review form is the contract, not this parser. Tuned
 * against real invoices during the RoofHub backfill; keep patterns permissive.
 */
export function parseSrsInvoice(text: string): ParsedInvoice {
  const out: ParsedInvoice = {
    invoice_number: null, order_number: null, invoice_date: null, due_date: null,
    terms: null, job_name: null, job_address: null, subtotal: null, tax: null,
    total: null, line_items: [], fields_found: 0,
  };
  if (!text.trim()) return out;
  const lines = text.split(/\n/);

  const grab = (re: RegExp): string | null => {
    for (const l of lines) { const m = l.match(re); if (m) return m[1].trim(); }
    return null;
  };

  out.invoice_number = grab(/invoice\s*(?:#|no\.?|num(?:ber)?)?\s*[:\s]\s*([A-Z0-9][A-Z0-9\-\/\.]{3,})/i);
  out.order_number = grab(/(?:order|so|sales order)\s*(?:#|no\.?|num(?:ber)?)?\s*[:\s]\s*([A-Z0-9][A-Z0-9\-\/\.]{3,})/i);

  const invDate = grab(/invoice\s*date\s*[:\s]\s*(\d{1,2}[\/-]\d{1,2}[\/-]\d{2,4})/i)
    ?? grab(/\bdate\s*[:\s]\s*(\d{1,2}[\/-]\d{1,2}[\/-]\d{2,4})/i);
  if (invDate) out.invoice_date = usDateToIso(invDate);
  const dueDate = grab(/due\s*date\s*[:\s]\s*(\d{1,2}[\/-]\d{1,2}[\/-]\d{2,4})/i);
  if (dueDate) out.due_date = usDateToIso(dueDate);
  out.terms = grab(/terms\s*[:\s]\s*(net\s*\d+|[A-Za-z0-9 ]{2,20})\b/i);

  out.job_name = grab(/job\s*(?:name|id)?\s*[:\s]\s*(.{3,60})/i);
  // "Ship To" block: take the first street-looking line after the marker.
  const shipIdx = lines.findIndex((l) => /ship\s*to/i.test(l));
  if (shipIdx >= 0) {
    for (let i = shipIdx; i < Math.min(shipIdx + 5, lines.length); i++) {
      const m = lines[i].match(/(\d{1,6}\s+[A-Za-z0-9 .'-]{4,50}(?:\b(?:rd|dr|ln|ct|way|st|ave|blvd|cir|trl|pkwy|loop|xing|pl|ter|hwy)\b\.?[A-Za-z0-9 ,.'-]*)?)/i);
      if (m) { out.job_address = m[1].trim(); break; }
    }
  }

  // Totals — labeled matches only, so a qty column can't win.
  const sub = grab(/sub\s*-?\s*total\s*[:\s$]*([\d,]+\.\d{2})/i);
  out.subtotal = money(sub);
  const tax = grab(/(?:sales\s*)?tax\s*[:\s$]*([\d,]+\.\d{2})/i);
  out.tax = money(tax);
  const totals: number[] = [];
  for (const l of lines) {
    const m = l.match(/(?:invoice\s*total|total\s*due|amount\s*due|balance\s*due|\btotal\b)\s*[:\s$]*([\d,]+\.\d{2})/i);
    const v = money(m?.[1]);
    if (v !== null) totals.push(v);
  }
  if (totals.length) out.total = Math.max(...totals);

  // Line items: rows shaped like  DESC  QTY [UOM] [UNIT] AMOUNT
  const uomAlt = UOMS.join("|");
  const lineRe = new RegExp(
    `^(.{4,70}?)\\s{2,}(\\d{1,4}(?:\\.\\d{1,2})?)\\s*(${uomAlt})?\\s{1,}\\$?([\\d,]+\\.\\d{2,4})(?:\\s{1,}\\$?([\\d,]+\\.\\d{2}))?$`, "i");
  for (const l of lines) {
    const m = l.trim().match(lineRe);
    if (!m) continue;
    const desc = m[1].trim();
    if (/total|subtotal|invoice|balance|payment|tax/i.test(desc)) continue;
    const qty = Number(m[2]);
    const unit = money(m[4]);
    const amt = money(m[5]) ?? (unit !== null && Number.isFinite(qty) ? Math.round(unit * qty * 100) / 100 : null);
    out.line_items.push({
      description: desc,
      qty: Number.isFinite(qty) ? qty : null,
      uom: m[3] ? m[3].toUpperCase() : null,
      unit_price: m[5] ? unit : null, // when only one $ column matched it's the amount, not unit
      amount: m[5] ? amt : unit,
    });
  }

  out.fields_found = [out.invoice_number, out.invoice_date, out.total, out.job_address ?? out.job_name]
    .filter((v) => v !== null && v !== undefined).length;
  return out;
}

/**
 * Squares from shingle-bundle lines: field-roof bundles ÷ 3. Hip & ridge and
 * starter bundles are deliberately EXCLUDED — they'd overstate squares.
 * Returns null when no shingle line is recognizable (never fake 0).
 */
export function computeSquaresFromLines(items: MaterialInvoiceLine[]): number | null {
  let bundles = 0;
  for (const it of items) {
    const d = (it.description || "").toLowerCase();
    const isShingle = /(duration|oakridge|timberline|supreme|heritage|landmark|hdz|shingle)/.test(d);
    const isAccessory = /(hip|ridge|starter|cap\b)/.test(d);
    const uomOk = !it.uom || /^(BD|BDL|EA)$/i.test(it.uom);
    if (isShingle && !isAccessory && uomOk && Number(it.qty) > 0) bundles += Number(it.qty);
  }
  if (bundles <= 0) return null;
  return Math.round((bundles / 3) * 10) / 10;
}
